import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Typography,
    Alert,
    CircularProgress,
    IconButton,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createCity, updateCity } from '../../store/slices/citySlice';
import { useTheme } from '../../contexts/ThemeContext';

interface CityFormProps {
    open: boolean;
    onClose: (isSuccess?: boolean, isEdit?: boolean, cityName?: string) => void;
    onSuccess?: () => void;
    onError?: (message: string) => void;
    city?: any;
    mode: 'create' | 'edit';
}

const CityForm: React.FC<CityFormProps> = ({ open, onClose, onSuccess, onError, city, mode }) => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    const { loading, error, zones } = useAppSelector((state) => state.cities);

    const [formData, setFormData] = useState({
        name: '',
        code: '',
        zone: '',
        status: 'active'
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (open) {
            if (mode === 'edit' && city) {
                setFormData({
                    name: city.name || '',
                    code: city.code || '',
                    zone: city.zone || '',
                    status: city.status === 1 || city.status === '1' ? 'active' : 'inactive'
                });
            } else {
                setFormData({
                    name: '',
                    code: '',
                    zone: zones.length > 0 ? zones[0] : '',
                    status: 'active'
                });
            }
            setErrors({});
        }
    }, [open, mode, city, zones]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'City name is required';
        }

        if (!formData.code.trim()) {
            newErrors.code = 'City code is required';
        }

        if (!formData.zone.trim()) {
            newErrors.zone = 'Zone is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            const cityData = {
                name: formData.name,
                code: formData.code,
                zone: formData.zone,
                status: formData.status === 'active' ? 1 : 0
            };

            if (mode === 'create') {
                await dispatch(createCity(cityData)).unwrap();
                onSuccess?.();
                onClose(true, false, formData.name);
            } else {
                await dispatch(updateCity({ id: city.id, data: cityData })).unwrap();
                onSuccess?.();
                onClose(true, true, formData.name);
            }
        } catch (error: any) {
            console.error('Error saving city:', error);
            onError?.(error?.message || 'An error occurred while saving the city');
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    minHeight: isMobile ? '100vh' : 'auto'
                }
            }}
        >
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <DialogTitle sx={{ 
                    p: isMobile ? 2 : 3, 
                    mb:2,
                    pb: isMobile ? 1 : 2,
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {mode === 'create' ? 'Add New City' : 'Edit City'}
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        disabled={loading}
                        sx={{ minWidth: 'auto', p: 1 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: isMobile ? 2 : 3, pt: 3 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="City Name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                error={!!errors.name}
                                helperText={errors.name || "Enter the full name of the city"}
                                disabled={loading}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="City Code"
                                value={formData.code}
                                onChange={(e) => handleInputChange('code', e.target.value)}
                                error={!!errors.code}
                                helperText={errors.code || "Enter a unique code for the city (e.g., KHI for Karachi)"}
                                disabled={loading}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth required error={!!errors.zone}>
                                <InputLabel>Zone</InputLabel>
                                <Select
                                    value={formData.zone}
                                    onChange={(e) => handleInputChange('zone', e.target.value)}
                                    disabled={loading}
                                    label="Zone"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    {zones.map((zone) => (
                                        <MenuItem key={zone} value={zone}>
                                            {zone}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.zone && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                        {errors.zone}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth required error={!!errors.status}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={formData.status}
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                    disabled={loading}
                                    label="Status"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                                {errors.status && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                        {errors.status}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ 
                    p: isMobile ? 2 : 3, 
                    borderTop: `1px solid ${theme.palette.divider}`,
                    gap: 1,
                    flexDirection: isMobile ? 'column' : 'row'
                }}>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        fullWidth={isMobile}
                        sx={{ 
                            color: theme.palette.text.secondary,
                            borderRadius: 3
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        fullWidth={isMobile}
                        sx={{
                            bgcolor: theme.palette.secondary.main,
                            borderRadius: 3,
                            '&:hover': {
                                bgcolor: theme.palette.secondary.dark,
                            }
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            mode === 'create' ? 'Add City' : 'Update City'
                        )}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CityForm;
